<script setup lang="ts">
import { useIntervalFn } from '@vueuse/core'
import { AlertTriangle, MessageCircle, Phone, Star, WifiOff } from 'lucide-vue-next'
import { computed, nextTick, ref, watch } from 'vue'
import { toast } from 'vue-sonner'

import ChatComposer from '@/components/chat/ChatComposer.vue'
import MessageBubble from '@/components/chat/MessageBubble.vue'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { settingsTokensPath } from '@/routes'

interface ChatMessage {
  id: number
  direction: 'outbound' | 'inbound'
  status: 'pending' | 'sent' | 'delivered' | 'failed'
  message_type: 'chat' | 'transactional' | 'review_invite'
  body: string
  sent_at: string
  read_at: string | null
  podium_message_uid: string | null
  channel_identifier: string
  sender_user_id: number | null
  sender_name: string | null
  loan_application_id: string | null
}

interface Props {
  open: boolean
  customerId: number
  customerName: string
  customerPhone: string
  customerFirstName: string
  loanApplicationId?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  loanApplicationId: null,
})

const emit = defineEmits<{
  'update:open': [value: boolean]
}>()

// ── State ─────────────────────────────────────────────────────────────────────
const messages      = ref<ChatMessage[]>([])
const loading       = ref(false)
const sending       = ref(false)
const sendingReview = ref(false)
const error         = ref<string | null>(null)
const notConnected  = ref(false)
const notConnectedAdmin = ref(false)
const threadEl      = ref<HTMLElement | null>(null)

// ── API helpers ───────────────────────────────────────────────────────────────
const messagesUrl = computed(() => `/api/chat/${props.customerId}/messages`)

function csrfToken(): string {
  return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? ''
}

async function fetchMessages() {
  try {
    const res = await fetch(messagesUrl.value, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })

    if (res.status === 503) {
      const data = await res.json().catch(() => ({})) as { error?: string; admin?: boolean }
      if (data.error === 'not_connected') {
        notConnected.value = true
        notConnectedAdmin.value = data.admin ?? false
        return
      }
    }

    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    notConnected.value = false
    const data = await res.json()
    messages.value = data.messages
  } catch {
    error.value = 'Could not load messages.'
  }
}

async function sendMessage(body: string) {
  sending.value = true
  try {
    const url = new URL(messagesUrl.value, window.location.origin)
    if (props.loanApplicationId) url.searchParams.set('loan_application_id', props.loanApplicationId)

    const res = await fetch(url.toString(), {
      method:      'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type':  'application/json',
        Accept:          'application/json',
        'X-CSRF-Token':  csrfToken(),
      },
      body: JSON.stringify({ body }),
    })

    if (!res.ok) {
      const data = await res.json().catch(() => ({})) as { error?: string }
      throw new Error(data.error || `HTTP ${res.status}`)
    }

    const msg: ChatMessage = await res.json() as ChatMessage
    messages.value.push(msg)
    scrollToBottom()
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Unknown error'
    toast.error('Failed to send message', { description: message })
  } finally {
    sending.value = false
  }
}

async function sendReviewInvite() {
  sendingReview.value = true
  try {
    const url = new URL(`/api/chat/${props.customerId}/review_invite`, window.location.origin)
    if (props.loanApplicationId) url.searchParams.set('loan_application_id', props.loanApplicationId)

    const res = await fetch(url.toString(), {
      method:      'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept:         'application/json',
        'X-CSRF-Token': csrfToken(),
      },
    })

    if (!res.ok) {
      const data = await res.json().catch(() => ({})) as { error?: string }
      throw new Error(data.error || `HTTP ${res.status}`)
    }

    const msg: ChatMessage = await res.json() as ChatMessage
    messages.value.push(msg)
    scrollToBottom()
    toast.success('Review request sent')
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Unknown error'
    toast.error('Could not send review request', { description: message })
  } finally {
    sendingReview.value = false
  }
}

async function retryMessage(id: number) {
  const msg = messages.value.find((m) => m.id === id)
  if (!msg) return
  messages.value = messages.value.filter((m) => m.id !== id)
  await sendMessage(msg.body)
}

// ── "Request Review" button state ─────────────────────────────────────────────
// Disabled when a review invite was already sent within the last 7 days.
const recentReviewSent = computed(() => {
  const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000
  return messages.value.some(
    (m) => m.message_type === 'review_invite' && new Date(m.sent_at).getTime() > sevenDaysAgo,
  )
})

// ── Polling ───────────────────────────────────────────────────────────────────
const { pause, resume } = useIntervalFn(fetchMessages, 5000, { immediate: false })

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      loading.value = true
      error.value = null
      notConnected.value = false
      fetchMessages().finally(() => {
        loading.value = false
        nextTick(scrollToBottom)
      })
      resume()
    } else {
      pause()
    }
  },
)

// ── Scroll helpers ────────────────────────────────────────────────────────────
function scrollToBottom() {
  nextTick(() => {
    if (threadEl.value) {
      threadEl.value.scrollTop = threadEl.value.scrollHeight
    }
  })
}

watch(() => messages.value.length, scrollToBottom)

// ── Grouped messages (group consecutive same-direction within 2 min) ───────────
function parseDate(s: string): Date | null {
  try {
    const d = new Date(s)
    return isNaN(d.getTime()) ? null : d
  } catch {
    return null
  }
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

function formatDayLabel(d: Date): string {
  return d.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })
}

interface MessageWithMeta extends ChatMessage {
  isGrouped: boolean
  showDateSeparator: boolean
  dateSeparatorLabel: string
}

const enrichedMessages = computed<MessageWithMeta[]>(() => {
  return messages.value.map((msg, i) => {
    const prev = messages.value[i - 1]
    const currDate = parseDate(msg.sent_at)
    const prevDate = prev ? parseDate(prev.sent_at) : null

    const showDateSeparator =
      !prevDate || !currDate || !isSameDay(currDate, prevDate)

    const dateSeparatorLabel = currDate ? formatDayLabel(currDate) : ''

    // Review invites and transactional messages break grouping
    const typeBreaksGroup = msg.message_type !== 'chat' || (prev?.message_type !== 'chat')

    const isGrouped =
      !typeBreaksGroup &&
      !!prev &&
      prev.direction === msg.direction &&
      !!currDate &&
      !!prevDate &&
      (currDate.getTime() - prevDate.getTime()) < 2 * 60 * 1000 &&
      !showDateSeparator

    return { ...msg, isGrouped, showDateSeparator, dateSeparatorLabel }
  })
})

// ── Connection status dot ─────────────────────────────────────────────────────
const lastMessage = computed(() => messages.value[messages.value.length - 1])
const connectionStatus = computed(() => {
  if (!lastMessage.value) return 'idle'
  if (lastMessage.value.direction === 'outbound' && lastMessage.value.status === 'failed') return 'failed'
  return 'ok'
})
</script>

<template>
  <Sheet :open="open" @update:open="emit('update:open', $event)">
    <SheetContent
      side="right"
      class="flex w-full flex-col gap-0 p-0 sm:max-w-md"
    >
      <!-- ── Header ──────────────────────────────────────────────────── -->
      <SheetHeader class="border-b px-4 py-3">
        <div class="flex items-start justify-between">
          <div class="flex items-center gap-3">
            <!-- Avatar -->
            <div class="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
              <MessageCircle class="size-5 text-primary" />
            </div>

            <div class="min-w-0">
              <SheetTitle class="truncate text-sm font-semibold">
                {{ customerName }}
              </SheetTitle>
              <SheetDescription class="mt-0.5 flex items-center gap-1.5 text-xs">
                <Phone class="size-3" />
                {{ customerPhone }}
                <span class="text-muted-foreground/50">·</span>
                <span class="text-muted-foreground">via Podium</span>

                <!-- Connection status dot -->
                <span
                  v-if="!notConnected"
                  :class="[
                    'inline-block size-1.5 rounded-full',
                    connectionStatus === 'ok'     ? 'bg-emerald-500' :
                    connectionStatus === 'failed' ? 'bg-destructive' :
                    'bg-muted-foreground/40'
                  ]"
                  :title="connectionStatus === 'failed' ? 'Last message failed' : 'Connected'"
                />
              </SheetDescription>
            </div>
          </div>
        </div>
      </SheetHeader>

      <!-- ── Not-connected banner ───────────────────────────────────── -->
      <div
        v-if="notConnected"
        class="flex items-start gap-3 border-b bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-300"
      >
        <AlertTriangle class="mt-0.5 size-4 shrink-0" />
        <div>
          <p class="font-medium">Podium not connected</p>
          <p class="text-xs opacity-80 mt-0.5">
            <template v-if="notConnectedAdmin">
              The Podium account needs to be connected before messages can be sent or viewed.
              <a
                :href="settingsTokensPath()"
                class="underline underline-offset-2 font-medium"
              >Connect now →</a>
            </template>
            <template v-else>
              Contact your admin to connect the Podium account.
            </template>
          </p>
        </div>
      </div>

      <!-- ── Thread body ─────────────────────────────────────────────── -->
      <div
        ref="threadEl"
        class="flex-1 overflow-y-auto px-4 py-4"
      >
        <!-- Loading skeleton -->
        <div v-if="loading" class="space-y-3">
          <div
            v-for="i in 4"
            :key="i"
            :class="[
              'h-10 animate-pulse rounded-2xl bg-muted',
              i % 2 === 0 ? 'ml-auto w-3/4' : 'w-3/4',
            ]"
          />
        </div>

        <!-- Error state -->
        <div
          v-else-if="error"
          class="flex flex-col items-center gap-2 py-12 text-center text-sm text-muted-foreground"
        >
          <WifiOff class="size-8 opacity-40" />
          <p>{{ error }}</p>
        </div>

        <!-- Empty state (only when connected) -->
        <div
          v-else-if="enrichedMessages.length === 0 && !notConnected"
          class="flex flex-col items-center gap-3 py-16 text-center"
        >
          <div class="flex size-16 items-center justify-center rounded-full bg-primary/10">
            <MessageCircle class="size-8 text-primary" />
          </div>
          <div>
            <p class="font-medium text-foreground">Start the conversation</p>
            <p class="mt-1 text-sm text-muted-foreground">
              Send {{ customerFirstName }} a message — it arrives as an SMS.
            </p>
          </div>
        </div>

        <!-- Messages -->
        <template v-else-if="!notConnected">
          <template v-for="msg in enrichedMessages" :key="msg.id">
            <!-- Date separator -->
            <div
              v-if="msg.showDateSeparator"
              class="my-4 flex items-center gap-3"
            >
              <div class="h-px flex-1 bg-border" />
              <span class="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                {{ msg.dateSeparatorLabel }}
              </span>
              <div class="h-px flex-1 bg-border" />
            </div>

            <MessageBubble
              :id="msg.id"
              :direction="msg.direction"
              :status="msg.status"
              :message-type="msg.message_type"
              :body="msg.body"
              :sent-at="msg.sent_at"
              :sender-name="msg.sender_name"
              :is-grouped="msg.isGrouped"
              @retry="retryMessage"
            />
          </template>
        </template>
      </div>

      <!-- ── Review invite + Composer (hidden when not connected) ──────── -->
      <template v-if="!notConnected">
        <!-- Request Review button -->
        <div class="border-t px-4 pt-3 pb-0">
          <Button
            variant="outline"
            size="sm"
            class="w-full gap-2"
            :disabled="sendingReview || recentReviewSent"
            :title="recentReviewSent ? 'Review already requested recently' : 'Send a Podium review invite via SMS'"
            @click="sendReviewInvite"
          >
            <Star class="size-4" :class="recentReviewSent ? 'text-yellow-400' : 'text-yellow-500'" />
            {{ recentReviewSent ? 'Review already requested' : 'Request Review' }}
          </Button>
        </div>

        <!-- Composer -->
        <ChatComposer
          :customer-first-name="customerFirstName"
          :disabled="sending"
          @send="sendMessage"
        />
      </template>
    </SheetContent>
  </Sheet>
</template>
